// Generic adjacency-list graph (ARCHITECTURE.md §3, src/graph).
//
// A directed graph keyed by node id. Nodes are GraphNode records; edges are
// GraphEdge records (from → to). The adjacency index maps each node id to the
// list of out-edges originating from it, giving O(1) neighbour lookup. This is
// the deterministic substrate every downstream module (AST, dependency,
// impact) builds on, so it stays free of any analysis-specific logic.

import type { GraphEdge, GraphNode } from "../types.js";

export class AdjacencyListGraph {
  /** Node id → node. */
  private readonly nodes = new Map<string, GraphNode>();

  /** Node id → out-edges (from === id). */
  private readonly adjacency = new Map<string, GraphEdge[]>();

  /** Total number of edges stored across all adjacency lists. */
  private edges = 0;

  /**
   * Insert or replace a node. Re-adding an existing id overwrites the stored
   * node but preserves its adjacency list, so edges survive a metadata update.
   */
  addNode(node: GraphNode): void {
    this.nodes.set(node.id, node);
    if (!this.adjacency.has(node.id)) {
      this.adjacency.set(node.id, []);
    }
  }

  /**
   * Add a directed edge (edge.from → edge.to). Endpoints are not required to
   * exist yet; missing endpoints are materialised as adjacency entries so the
   * edge is traversable. Duplicate edges are permitted — callers that need
   * deduplication should enforce it upstream.
   */
  addEdge(edge: GraphEdge): void {
    const list = this.adjacency.get(edge.from);
    if (list) {
      list.push(edge);
    } else {
      this.adjacency.set(edge.from, [edge]);
    }
    // Ensure the target id has an adjacency entry so it is reachable/iterable.
    if (!this.adjacency.has(edge.to)) {
      this.adjacency.set(edge.to, []);
    }
    this.edges += 1;
  }

  /** Return the node for `id`, or undefined if it was never added. */
  getNode(id: string): GraphNode | undefined {
    return this.nodes.get(id);
  }

  /** True if a node with `id` has been added. */
  hasNode(id: string): boolean {
    return this.nodes.has(id);
  }

  /**
   * Out-edges originating at `id` (a defensive copy). Unknown ids yield an
   * empty array rather than throwing.
   */
  getEdges(id: string): GraphEdge[] {
    const list = this.adjacency.get(id);
    return list ? [...list] : [];
  }

  /**
   * Ids of the direct successors of `id` (the `to` of each out-edge), in
   * insertion order. Duplicates are preserved to mirror parallel edges.
   */
  getNeighbors(id: string): string[] {
    const list = this.adjacency.get(id);
    return list ? list.map((edge) => edge.to) : [];
  }

  /** All nodes, in insertion order. */
  getNodes(): GraphNode[] {
    return [...this.nodes.values()];
  }

  /** All edges across every adjacency list. */
  getAllEdges(): GraphEdge[] {
    const all: GraphEdge[] = [];
    for (const list of this.adjacency.values()) {
      all.push(...list);
    }
    return all;
  }

  /** Number of nodes. */
  nodeCount(): number {
    return this.nodes.size;
  }

  /** Number of edges. */
  edgeCount(): number {
    return this.edges;
  }
}
